import { useEffect, useRef, useState } from "react";
import type { ApprovalView } from "../../shared/contracts";
import { ApprovalCard } from "../components/approval-card";
import { AgentAvatarView } from "../components/avatar";
import { RulesPanel } from "../components/rules-panel";
import { ErrorNotice, Loading, PageTop } from "../components/ui";
import { useSettings } from "../lib/agent";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/hooks";
import { useAgentEvents } from "../lib/socket";
import { mutateResource, useResource } from "../lib/store";

/**
 * /approbations — what waits for a yes or a no (V2Validations): one card each, the shape of the conversation's bubble
 * (a question, an inset preview, Oui / Non / « Toujours oui »). A card decided here stays in place as one faint line;
 * older decisions sit behind « Historique »; « Tout est validé. » and the character in joy when nothing waits. The
 * rules (« Tes règles ») follow.
 */
export function ApprovalsPage({ focus }: { focus?: string }) {
  usePageTitle("Validations");
  const settings = useSettings();
  const agent = { name: settings.data?.agentName ?? "Nova", avatar: settings.data?.avatar ?? null, character: settings.data?.character ?? null, main: true };
  const approvals = useResource<ApprovalView[]>(
    "approvals:all",
    async (signal) => (await api("GET /api/approvals", { query: { status: "all" }, signal })).approvals,
    { refreshOn: ["approval.upsert"] },
  );
  // Decided on this visit: kept in the list with their outcome instead of jumping to the history.
  const [decidedHere, setDecidedHere] = useState<string[]>([]);

  // Apply live updates right away (the debounced refetch then confirms them).
  useAgentEvents((e) => {
    if (e.type !== "approval.upsert") return;
    const updated = e.approval;
    approvals.mutate((list) => {
      if (!list) return list;
      return list.some((a) => a.id === updated.id) ? list.map((a) => (a.id === updated.id ? updated : a)) : [updated, ...list];
    });
    mutateResource<ApprovalView[]>("approvals:pending", (list) => {
      if (!list) return list;
      const rest = list.filter((a) => a.id !== updated.id);
      return updated.status === "pending" ? [updated, ...rest] : rest;
    });
  });

  const list = approvals.data ?? [];
  const current = list.filter((a) => a.status === "pending" || decidedHere.includes(a.id)).sort((a, b) => b.createdAt - a.createdAt);
  const pendingCount = current.filter((a) => a.status === "pending").length;
  const history = list
    .filter((a) => a.status !== "pending" && !decidedHere.includes(a.id))
    .sort((a, b) => (b.decidedAt ?? b.createdAt) - (a.decidedAt ?? a.createdAt))
    .slice(0, 30);

  const focusInHistory = Boolean(focus && history.some((a) => a.id === focus));
  const [showHistory, setShowHistory] = useState(focusInHistory);
  useEffect(() => {
    if (focusInHistory) setShowHistory(true);
  }, [focusInHistory]);

  // Deep link from a notification: bring the card into view once it is rendered.
  const scrolled = useRef<string | null>(null);
  useEffect(() => {
    if (!focus || scrolled.current === focus || !list.some((a) => a.id === focus)) return;
    scrolled.current = focus;
    document.getElementById(`approval-${focus}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focus, list, showHistory]);

  return (
    <div className="mx-auto flex max-w-[600px] flex-col gap-3">
      <PageTop title="Validations" className="mb-2" after={pendingCount ? <span className="text-[14px] text-subtle">{pendingCount}</span> : null} />

      {approvals.loading ? (
        <Loading />
      ) : !approvals.data ? (
        <ErrorNotice message={approvals.error?.message ?? "Impossible de charger les validations."} onRetry={() => void approvals.reload()} />
      ) : (
        <>
          {approvals.error ? <ErrorNotice message={approvals.error.message} onRetry={() => void approvals.reload()} /> : null}
          {current.map((a) => (
            <ApprovalCard key={a.id} approval={a} focused={a.id === focus} onDecided={(u) => setDecidedHere((ids) => (ids.includes(u.id) ? ids : [...ids, u.id]))} />
          ))}
          {pendingCount === 0 ? (
            <div className="flex flex-col items-center gap-2.5 pt-12 pb-6 text-center">
              <AgentAvatarView agent={agent} size={120} bare mood="joie" />
              <p className="font-display text-[24px]">Tout est validé.</p>
            </div>
          ) : null}

          {history.length ? (
            <section aria-label="Historique" className="mt-4 flex flex-col gap-1">
              <button type="button" aria-expanded={showHistory} onClick={() => setShowHistory((v) => !v)} className="inline-flex min-h-11 items-center self-start px-1 text-[13px] font-semibold text-subtle hover:text-fg">
                Historique
              </button>
              {showHistory ? history.map((a) => <ApprovalCard key={a.id} approval={a} focused={a.id === focus} />) : null}
            </section>
          ) : null}

          <div className="mt-6">
            <RulesPanel />
          </div>
        </>
      )}
    </div>
  );
}
